import { useEffect, useState } from 'react'
import { fetchPrices } from '../utils/api'
import { calculatePriceInsights } from '../utils/priceInsights'
import './PriceInsights.css'

function PriceInsights({ selectedDate, currentPrices, includeVAT, onInsightsCalculated }) {
  const [historicalData, setHistoricalData] = useState([])
  const [loading, setLoading] = useState(false)
  const [insightsResult, setInsightsResult] = useState(null)

  const vatMultiplier = includeVAT ? 1.21 : 1.0

  useEffect(() => {
    if (selectedDate) {
      fetchHistoricalAndCalculate(selectedDate)
    }
  }, [selectedDate, currentPrices])

  const fetchHistoricalAndCalculate = async (date) => {
    setLoading(true)
    try {
      const toDate = new Date(date)
      const fromDate = new Date(date)
      fromDate.setDate(fromDate.getDate() - 14) // Šuolis į 14 parų atgal (nuo ANALIZUOJAMOS datos)

      const fromStr = formatDate(fromDate)
      const toStr = formatDate(toDate)

      const result = await fetchPrices(fromStr, toStr)
      
      if (result.prices) {
        const histPrices = Object.entries(result.prices).map(([dt, price]) => ({
          datetime: dt,
          price: price
        }))

        // Analizuojame pagal 336h (14 parų) langą
        const insights = calculatePriceInsights(currentPrices, histPrices)
        setInsightsResult(insights)
        
        if (onInsightsCalculated) {
          onInsightsCalculated(insights)
        }
      }
    } catch (err) {
      console.error('Klaida skaičiuojant įžvalgas:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  if (loading || !insightsResult) {
    return <div className="price-insights loading">Skaičiuojamos įžvalgos (14 parų langas)...</div>
  }

  const { insights, stats } = insightsResult

  const formatIntervals = (hours) => {
    if (!hours || hours.length === 0) return '';
    const sorted = [...hours].sort((a, b) => a - b);
    const intervals = [];
    let start = sorted[0];
    let end = sorted[0];

    for (let i = 1; i <= sorted.length; i++) {
      if (i < sorted.length && sorted[i] === end + 1) {
        end = sorted[i];
      } else {
        intervals.push(start === end ? `${start}:00` : `${start}:00 - ${end + 1}:00`);
        if (i < sorted.length) {
          start = sorted[i];
          end = sorted[i];
        }
      }
    }
    return intervals.join(', ');
  }

  const cheapHours = insights
    .filter(i => i.category === 'CRITICAL_CHEAP' || i.category === 'CHEAP')
    .map(i => i.hour);

  const expensiveHours = insights
    .filter(i => i.category === 'CRITICAL_EXPENSIVE' || i.category === 'EXPENSIVE')
    .map(i => i.hour);

  let bestWindow = { start: 0, avg: Infinity };
  if (insights.length >= 3) {
    for (let i = 0; i <= insights.length - 3; i++) {
      const window = insights.slice(i, i + 3);
      const avg = window.reduce((sum, item) => sum + item.price, 0) / 3;
      if (avg < bestWindow.avg) {
        bestWindow = { start: i, avg: avg };
      }
    }
  }

  return (
    <div className="price-insights">
      <div className="insights-header">
        <h3>💡 Vartojimo rekomendacijos (14 d. analizė)</h3>
        <div className="stats-summary">
          <span className="stat-tag" title="25% pigiausių valandų riba">P25: {(stats.p25 * vatMultiplier).toFixed(3)}</span>
          <span className="stat-tag" title="Vidurinė kaina">P50: {(stats.p50 * vatMultiplier).toFixed(3)}</span>
          <span className="stat-tag" title="25% brangiausių valandų riba">P75: {(stats.p75 * vatMultiplier).toFixed(3)}</span>
        </div>
      </div>

      <div className="intervals-container">
        {cheapHours.length > 0 ? (
          <div className="interval-group cheap-group">
            <span className="group-label">🟢 PIGIOS VALANDOS:</span>
            <span className="group-value">{formatIntervals(cheapHours)}</span>
          </div>
        ) : (
          <div className="interval-group cheap-group">
            <span className="group-label">🟢 PIGIOS VALANDOS:</span>
            <span className="group-value">Šiandien nėra</span>
          </div>
        )}
        
        {expensiveHours.length > 0 ? (
          <div className="interval-group expensive-group">
            <span className="group-label">🔴 BRANGIOS VALANDOS:</span>
            <span className="group-value">{formatIntervals(expensiveHours)}</span>
          </div>
        ) : (
          <div className="interval-group expensive-group">
            <span className="group-label">🔴 BRANGIOS VALANDOS:</span>
            <span className="group-value">Šiandien nėra</span>
          </div>
        )}
      </div>

      <div className="recommendation-box">
        <h4>📢 Verdiktas šiai dienai</h4>
        <p>
          {stats.isCrisis 
            ? `⚠️ Šiandien biržos kainos yra neįprastai aukštos (viršija 14 parų brangiausią ketvirtį). Rekomenduojama vartoti minimaliai. Pigiausias 3 valandų langas: ${bestWindow.start}:00 - ${bestWindow.start + 3}:00 val.`
            : cheapHours.length > 0 
              ? `✅ Palankiausias metas vartojimui: ${formatIntervals(cheapHours)} val. Šiuo metu kainos geriausios per 14 parų. Optimalus 3 valandų blokas: ${bestWindow.start}:00 - ${bestWindow.start + 3}:00 val.`
              : expensiveHours.length > 0
                ? `⚠️ Šiandien rekomenduojama vengti: ${formatIntervals(expensiveHours)} val. Kitomis valandomis kainos yra normalios. Optimaliausias metas: ${bestWindow.start}:00 - ${bestWindow.start + 3}:00 val.`
                : `ℹ️ Šiandienos kainos yra stabilios ir artimos 14 parų vidurkiui. Efektyviausia vartoti intervale: ${bestWindow.start}:00 - ${bestWindow.start + 3}:00 val.`}
        </p>
      </div>
    </div>
  )
}

export default PriceInsights
